import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Footer } from "@/components/footer";
import { Header } from "@/components/header";

export function BookPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [availability, setAvailability] = useState<Record<number, number>>({});
  const [totalVillas, setTotalVillas] = useState(8);
  const [isLoading, setIsLoading] = useState(true);

  // Load availability data for current month
  useEffect(() => {
    fetchAvailability(currentYear, currentMonth + 1);
  }, [currentMonth, currentYear]);

  const fetchAvailability = async (year: number, month: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/calendar/availability/${year}/${month}`);
      const data = (await response.json()) as {
        success: boolean;
        availability: Record<number, number>;
        totalVillas: number;
      };
      if (data.success) {
        setAvailability(data.availability);
        setTotalVillas(data.totalVillas);
      }
    } catch (error) {
      console.error("Failed to fetch availability:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDayClick = (day: number) => {
    const clickedDate = new Date(currentYear, currentMonth, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Don't allow selecting past dates
    if (clickedDate < today) return;

    // Don't allow selecting sold out dates
    if (availability[day] === 0) return;

    if (!checkInDate || (checkInDate && checkOutDate)) {
      // Start new selection
      setCheckInDate(clickedDate);
      setCheckOutDate(null);
    } else if (clickedDate > checkInDate) {
      // Set check-out date
      setCheckOutDate(clickedDate);
    } else {
      // If clicked date is before check-in, reset
      setCheckInDate(clickedDate);
      setCheckOutDate(null);
    }
  };

  const handleContinue = () => {
    if (checkInDate && checkOutDate) {
      const checkIn = checkInDate.toISOString().split("T")[0];
      const checkOut = checkOutDate.toISOString().split("T")[0];

      // Get pre-selected villa from URL if exists
      const preSelectedVilla = searchParams.get("villa");

      if (preSelectedVilla) {
        // Skip villa selection, go directly to guest details
        navigate(
          `/book/guest-details?checkIn=${checkIn}&checkOut=${checkOut}&villaId=${preSelectedVilla}`
        );
      } else {
        // Normal flow: go to villa selection
        navigate(`/book/select-villa?checkIn=${checkIn}&checkOut=${checkOut}`);
      }
    }
  };

  // Get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
  const monthName = new Date(currentYear, currentMonth).toLocaleString("default", {
    month: "long",
  });

  // Calculate nights
  const nights =
    checkInDate && checkOutDate
      ? Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0;

  // Get availability for selected dates
  const getSelectedDateAvailability = () => {
    if (!checkInDate || !checkOutDate) return null;

    let minAvailable = totalVillas;
    const start = checkInDate.getDate();
    const end = checkOutDate.getDate();

    for (let day = start; day < end; day++) {
      const avail = availability[day] || 0;
      if (avail < minAvailable) minAvailable = avail;
    }

    return minAvailable;
  };

  const selectedAvailability = getSelectedDateAvailability();

  const isDateInRange = (day: number) => {
    if (!checkInDate) return false;
    const date = new Date(currentYear, currentMonth, day);
    if (checkOutDate) {
      return date >= checkInDate && date <= checkOutDate;
    }
    return date.getTime() === checkInDate.getTime();
  };

  const isDayPast = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getAvailabilityColor = (available: number) => {
    if (available === 0) return "bg-red-50 border-red-300 opacity-50";
    if (available <= totalVillas / 3) return "bg-yellow-50 border-yellow-300";
    return "bg-green-50 border-green-300";
  };

  const getAvailabilityTextColor = (available: number) => {
    if (available === 0) return "text-red-700";
    if (available <= totalVillas / 3) return "text-yellow-700";
    return "text-green-700";
  };

  // Get pre-selected villa info
  const preSelectedVilla = searchParams.get("villa");

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Header />

      <main className="flex-1 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-['Libre_Baskerville'] text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Book Your Stay
            </h1>
            {preSelectedVilla ? (
              <div className="flex items-center justify-center gap-2">
                <p className="text-stone-600 text-lg">
                  Booking{" "}
                  <span className="font-semibold text-stone-900">Villa {preSelectedVilla}</span> ·
                  Select your dates
                </p>
              </div>
            ) : (
              <p className="text-stone-600 text-lg">Select your dates to see available villas</p>
            )}
          </div>

          <div className="bg-white border border-stone-200 p-6 md:p-8 rounded-lg">
            {isLoading ? (
              // Loading skeleton
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  {/* Month Navigation Skeleton */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-8 w-28 bg-stone-200 rounded animate-pulse" />
                    <div className="h-8 w-40 bg-stone-200 rounded animate-pulse" />
                    <div className="h-8 w-28 bg-stone-200 rounded animate-pulse" />
                  </div>

                  {/* Calendar Grid Skeleton */}
                  <div className="grid grid-cols-7 gap-2">
                    {/* Day headers */}
                    {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
                      <div
                        key={day}
                        className="text-center font-semibold text-xs text-stone-500 py-2"
                      >
                        {day}
                      </div>
                    ))}

                    {/* Skeleton days */}
                    {Array.from({ length: 35 }).map((_, i) => (
                      <div
                        key={i}
                        className="aspect-square p-3 border-2 border-stone-200 rounded flex flex-col justify-between"
                      >
                        <div className="h-4 w-6 bg-stone-200 rounded animate-pulse" />
                        <div className="h-3 w-full bg-stone-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex items-center justify-center gap-2 text-stone-500">
                    <div className="animate-spin rounded-full size-5 border-2 border-stone-300 border-t-stone-600" />
                    <span className="text-sm">Loading availability...</span>
                  </div>
                </div>

                {/* Summary Skeleton */}
                <div className="lg:col-span-1">
                  <div className="bg-stone-50 p-6 rounded-lg border border-stone-200">
                    <div className="h-6 w-32 bg-stone-200 rounded animate-pulse mb-6" />
                    <div className="space-y-4">
                      <div className="h-20 bg-stone-200 rounded animate-pulse" />
                      <div className="h-20 bg-stone-200 rounded animate-pulse" />
                      <div className="h-12 bg-stone-200 rounded animate-pulse mt-6" />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Calendar */}
                <div className="lg:col-span-2">
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={handlePreviousMonth}
                      className="text-stone-600 hover:text-stone-900 transition-colors flex items-center gap-2"
                    >
                      <ChevronLeft className="size-5" />
                      <span className="hidden sm:inline">Previous</span>
                    </button>
                    <h3 className="font-semibold text-lg">
                      {monthName} {currentYear}
                    </h3>
                    <button
                      onClick={handleNextMonth}
                      className="text-stone-600 hover:text-stone-900 transition-colors flex items-center gap-2"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="size-5" />
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {/* Day headers */}
                    {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
                      <div
                        key={day}
                        className="text-center font-semibold text-xs text-stone-500 py-2"
                      >
                        {day}
                      </div>
                    ))}

                    {/* Empty cells for days before month starts */}
                    {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}

                    {/* Calendar days */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const available = availability[day] || 0;
                      const isPast = isDayPast(day);
                      const inRange = isDateInRange(day);
                      const isSoldOut = available === 0;

                      return (
                        <button
                          key={day}
                          onClick={() => handleDayClick(day)}
                          disabled={isPast || isSoldOut || isLoading}
                          className={`
                          p-3 border-2 rounded transition-all
                          ${isPast ? "bg-stone-100 border-stone-200 text-stone-400" : inRange ? "bg-stone-900 border-stone-900 text-white" : getAvailabilityColor(available)}
                          ${isPast || isSoldOut ? "cursor-not-allowed" : "cursor-pointer hover:scale-105"}
                          ${!isPast && !isSoldOut && !inRange ? "hover:border-stone-400" : ""}
                        `}
                        >
                          <div className="text-sm font-semibold">{day}</div>
                          {!isPast && !inRange && (
                            <div
                              className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 leading-tight ${inRange ? "text-white" : getAvailabilityTextColor(available)}`}
                            >
                              {isSoldOut ? "Full" : `${available}`}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="mt-6 flex gap-4 text-xs flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="size-4 bg-green-50 border border-green-300 rounded" />
                      <span>Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="size-4 bg-yellow-50 border border-yellow-300 rounded" />
                      <span>Limited</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="size-4 bg-red-50 border border-red-300 rounded" />
                      <span>Sold Out</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="size-4 bg-stone-900 rounded" />
                      <span>Selected</span>
                    </div>
                  </div>
                </div>

                {/* Summary Sidebar */}
                <div className="bg-stone-50 p-6 rounded-lg border border-stone-200 h-fit">
                  <h4 className="font-semibold mb-4">Your Selection</h4>

                  {checkInDate ? (
                    <div className="space-y-3 text-sm">
                      <div>
                        <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                          Check-in
                        </div>
                        <div className="font-semibold">
                          {checkInDate.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>
                      </div>

                      {checkOutDate && (
                        <>
                          <div>
                            <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                              Check-out
                            </div>
                            <div className="font-semibold">
                              {checkOutDate.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </div>
                          </div>

                          <div className="border-t border-stone-300 pt-3">
                            <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                              Duration
                            </div>
                            <div className="font-semibold">
                              {nights} {nights === 1 ? "night" : "nights"}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                              Available Villas
                            </div>
                            <div
                              className={`font-semibold ${
                                selectedAvailability === 0
                                  ? "text-red-700"
                                  : selectedAvailability && selectedAvailability <= totalVillas / 3
                                    ? "text-yellow-700"
                                    : "text-green-700"
                              }`}
                            >
                              {selectedAvailability}{" "}
                              {selectedAvailability === 1 ? "villa" : "villas"}
                            </div>
                          </div>

                          <button
                            onClick={handleContinue}
                            disabled={!selectedAvailability || selectedAvailability === 0}
                            className="w-full bg-stone-900 text-white px-6 py-3 mt-4 text-sm tracking-widest uppercase font-semibold hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Select Villa
                          </button>
                        </>
                      )}

                      {!checkOutDate && (
                        <p className="text-sm text-stone-600 mt-4">
                          Please select your check-out date
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-600">Select your check-in date to begin</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
